/**
 * Leitstand — die Bausteine der Verwaltungsebene.
 *
 * Umsetzung des Entwurfs vom 13.08.2026. Die Regeln dahinter stehen in
 * docs/OBERFLAECHE-ENTSCHEIDUNGEN.md; hier nur die drei, die beim Bauen
 * ständig gebraucht werden:
 *
 *   1. Alle Zahlen laufen in Festbreite mit tabular-nums (`font-zahl`),
 *      damit Ziffern untereinander stehen. Man liest Spalten, nicht Wörter.
 *   2. Farbe bedeutet ausschließlich Zustand — lauf, stand, stoer. Der
 *      Akzent sagt "hier kannst du handeln" und ist davon getrennt.
 *   3. Keine festen Farbwerte in den Bausteinen. Nur die Token-Utilities,
 *      sonst ist ein zweites Farbschema später nicht mehr möglich.
 *
 * Diese Bausteine gehören NICHT in die Erfassung an den Stationen — die
 * bleibt hell und unverändert.
 */

import type { ReactNode } from 'react';

/* ── Kleines Beschriftungs-Label über Werten und in Kartenköpfen ───────── */

export function Marke({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <span className={`font-zahl text-[10px] uppercase tracking-[0.14em] text-tinte-3 ${className}`}>
      {children}
    </span>
  );
}

/* ── Zahl in Festbreite. Für jeden Wert, den man mit anderen vergleicht ── */

export function Zahl({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <span className={`font-zahl tabular-nums ${className}`}>{children}</span>;
}

/* ── Kennzahl-Kachel: eine große Zahl mit Einordnung darunter ──────────── */

export type Tendenz = 'hoch' | 'runter' | 'neutral';

interface KennzahlProps {
  /** Worum es geht, klein darüber — z.B. "Gewaschen" */
  marke: string;
  /** Der Wert selbst, bereits formatiert — z.B. "98,96" */
  wert: string;
  /** Einheit, kleiner und leiser gesetzt — z.B. "t" */
  einheit?: string;
  /** Einordnung darunter — z.B. "+4,2 % zur Vorwoche" */
  neben?: string;
  /**
   * Färbt die Nebenzeile. `hoch` grün, `runter` rot — gemeint ist die
   * betriebliche Bewertung, nicht die Richtung: mehr Störzeit ist `runter`.
   */
  tendenz?: Tendenz;
  /** Für Zustände statt Zahlen ("läuft", "verbunden") — setzt den Wert kleiner */
  klein?: boolean;
}

export function Kennzahl({
  marke,
  wert,
  einheit,
  neben,
  tendenz = 'neutral',
  klein = false,
}: KennzahlProps) {
  const tendenzFarbe =
    tendenz === 'hoch' ? 'text-lauf' : tendenz === 'runter' ? 'text-stoer' : 'text-tinte-3';

  return (
    <div className="flex flex-col gap-1 rounded-leitstand border border-linie bg-flaeche px-[17px] py-[15px]">
      <Marke>{marke}</Marke>
      <span
        className={`font-zahl font-semibold tabular-nums leading-[1.05] tracking-[-0.03em] ${
          klein ? 'text-[22px]' : 'text-[38px]'
        }`}
      >
        {wert}
        {einheit && <span className="text-[16px] font-normal text-tinte-3"> {einheit}</span>}
      </span>
      {neben && <span className={`font-zahl text-[11.5px] ${tendenzFarbe}`}>{neben}</span>}
    </div>
  );
}

/* ── Karte: der Rahmen für alles Größere ───────────────────────────────── */

interface KarteProps {
  titel: string;
  /** Kleine Einordnung rechts im Kopf — z.B. "50 kg · 90 Tage" */
  marke?: string;
  children: ReactNode;
  /** Für Tabellen: Rumpf ohne Innenabstand, dafür seitlich scrollbar */
  tabelle?: boolean;
  className?: string;
}

export function Karte({ titel, marke, children, tabelle = false, className = '' }: KarteProps) {
  return (
    <section className={`rounded-leitstand border border-linie bg-flaeche ${className}`}>
      <div className="flex items-baseline justify-between gap-3 border-b border-linie px-[18px] py-[14px]">
        <h2 className="text-sm font-semibold tracking-[0.01em]">{titel}</h2>
        {marke && <Marke>{marke}</Marke>}
      </div>
      <div className={tabelle ? 'overflow-x-auto p-[18px]' : 'p-[18px]'}>{children}</div>
    </section>
  );
}

/* ── Zustand: Punkt plus Wort. Die einzige Stelle, wo Farbe etwas sagt ─── */

export type ZustandsArt = 'gut' | 'warnung' | 'stoerung';

const ZUSTAND_FARBE: Record<ZustandsArt, string> = {
  gut: 'text-lauf',
  warnung: 'text-stand',
  stoerung: 'text-stoer',
};

export function Zustand({ art, children }: { art: ZustandsArt; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-zahl text-[11px] ${ZUSTAND_FARBE[art]}`}
    >
      <i className="h-[7px] w-[7px] rounded-full bg-current" aria-hidden />
      {children}
    </span>
  );
}

/* ── Zeitbalken: woran die Zeit ging, in einem Streifen ────────────────── */

export interface Abschnitt {
  /** Anteil in Prozent */
  anteil: number;
  farbe: 'lauf' | 'stoer' | 'stand';
  /** Beschriftung in der Legende — z.B. "Laufzeit" */
  name: string;
  /** Absolutwert für die Legende — z.B. "6,9 h" */
  wert: string;
}

const ABSCHNITT_FLAECHE: Record<Abschnitt['farbe'], string> = {
  lauf: 'bg-lauf',
  stoer: 'bg-stoer',
  stand: 'bg-stand',
};

export function Zeitbalken({ abschnitte }: { abschnitte: Abschnitt[] }) {
  return (
    <div>
      <div className="flex h-10 overflow-hidden rounded-[5px]">
        {abschnitte.map((a) => (
          <div
            key={a.name}
            className={`flex items-center justify-center font-zahl text-xs font-semibold text-akzent-satz ${
              ABSCHNITT_FLAECHE[a.farbe]
            }`}
            style={{ width: `${a.anteil}%` }}
            title={`${a.name} ${a.wert}`}
          >
            {/* Schmale Abschnitte tragen keine Beschriftung — sie stünde über den Rand */}
            {a.anteil >= 10 ? `${a.anteil.toLocaleString('de-DE')} %` : ''}
          </div>
        ))}
      </div>
      <div className="mt-[13px] flex flex-wrap gap-4 text-[12.5px]">
        {abschnitte.map((a) => (
          <span key={a.name} className="flex items-center gap-[7px] text-tinte-2">
            <i
              className={`h-[9px] w-[9px] flex-none rounded-[2px] ${ABSCHNITT_FLAECHE[a.farbe]}`}
              aria-hidden
            />
            {a.name} <b className="font-zahl font-medium text-tinte">{a.wert}</b>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Rangliste: was am meisten kostet, mit Balken zum Vergleichen ──────── */

export interface RangEintrag {
  /** Kennnummer der Störung, klein vorangestellt */
  nr?: string;
  text: string;
  /** Der Wert, nach dem sortiert wird — z.B. "89,4 h" */
  wert: string;
  /** Anteil am Spitzenreiter, 0–100 */
  anteil: number;
  /** Zweitwert, leiser — z.B. "4 083 ×" */
  neben?: string;
}

export function Rangliste({ eintraege }: { eintraege: RangEintrag[] }) {
  return (
    <div>
      {eintraege.map((e, i) => (
        <div
          key={`${e.nr ?? ''}-${e.text}`}
          className={`grid grid-cols-[1fr_auto] gap-x-4 gap-y-[5px] py-3 ${
            i < eintraege.length - 1 ? 'border-b border-linie' : ''
          }`}
        >
          <div>
            {e.nr && <span className="mr-[9px] font-zahl text-[11px] text-tinte-3">{e.nr}</span>}
            {e.text}
          </div>
          <div className="whitespace-nowrap text-right font-zahl text-[17px]">{e.wert}</div>
          <div className="col-span-2 h-[7px] overflow-hidden rounded-[4px] bg-flaeche-2">
            <i className="block h-full rounded-[4px] bg-stoer" style={{ width: `${e.anteil}%` }} />
          </div>
          {e.neben && (
            <div className="col-span-2 text-right font-zahl text-[11.5px] text-tinte-3">
              {e.neben}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Anteilsliste: Verbrauch, Programmanteile — Wert plus Spur ─────────── */

export interface AnteilEintrag {
  name: string;
  /** Bereits formatiert — z.B. "24 466" oder "31,2 %" */
  wert: string;
  /** Anteil am Spitzenreiter, 0–100 */
  anteil: number;
}

export function Anteilsliste({ eintraege }: { eintraege: AnteilEintrag[] }) {
  return (
    <div className="flex flex-col gap-[11px]">
      {eintraege.map((e) => (
        <div key={e.name} className="grid grid-cols-[1fr_auto] items-baseline gap-3 text-sm">
          <span>{e.name}</span>
          <Zahl>{e.wert}</Zahl>
          <i className="col-span-2 h-[5px] rounded-[3px] bg-flaeche-2">
            <i className="block h-full rounded-[3px] bg-akzent" style={{ width: `${e.anteil}%` }} />
          </i>
        </div>
      ))}
    </div>
  );
}

/* ── Notiz: hebt eine Erklärung heraus, ohne zu alarmieren ─────────────── */

export function Notiz({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <div className="rounded-r-[5px] border-l-2 border-akzent bg-akzent-tief px-[15px] py-3 text-[13px]">
      <b className="mb-1 block font-zahl text-[10px] uppercase tracking-[0.13em] text-akzent">
        {titel}
      </b>
      {children}
    </div>
  );
}

/* ── Tabelle: Kopfzeile in Versalien, Zahlenspalten rechtsbündig ───────── */

export function Tabelle({ children }: { children: ReactNode }) {
  return <table className="w-full border-collapse text-sm">{children}</table>;
}

export function Kopfzelle({
  children,
  rechts = false,
}: {
  children?: ReactNode;
  rechts?: boolean;
}) {
  return (
    <th
      className={`border-b border-linie-2 pb-2.5 pr-3 font-zahl text-[10px] font-medium uppercase tracking-[0.13em] text-tinte-3 ${
        rechts ? 'pr-0 text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

export function Zelle({
  children,
  rechts = false,
  zahl = false,
  letzte = false,
}: {
  children?: ReactNode;
  rechts?: boolean;
  /** Setzt den Inhalt in Festbreite — für alles Vergleichbare */
  zahl?: boolean;
  /** Unterste Zeile trägt keine Trennlinie */
  letzte?: boolean;
}) {
  return (
    <td
      className={`py-[11px] pr-3 ${letzte ? '' : 'border-b border-linie'} ${
        rechts ? 'pr-0 text-right' : ''
      } ${zahl ? 'font-zahl tabular-nums' : ''}`}
    >
      {children}
    </td>
  );
}

/* ── Kennung: kleines Etikett, z.B. der Schichttyp in einer Tabelle ────── */

export function Kennung({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-[4px] bg-akzent-tief px-[7px] py-[3px] font-zahl text-[10px] tracking-[0.08em] text-akzent">
      {children}
    </span>
  );
}
